import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { lookupDeviceBySerial, lookupDeviceByServiceNumber } from "../services/api";

const PORTAL_BASE_URL = "https://neopharmportal.azurewebsites.net";

const SERIAL_NOT_FOUND_ERROR =
  "המספר הסידורי שגוי. אנא פנה/י למוקד שירות טכני של החברה בטל' 03-9371111.";
const SERVICE_NOT_FOUND_ERROR =
  "מספר השירות שגוי. אנא פנה/י למוקד שירות טכני של החברה בטל' 03-9371111.";
const GENERIC_ERROR = "שגיאה בחיפוש המכשיר. אנא נסה שוב מאוחר יותר.";


const DeviceLookup = () => {
  const [deviceNumber, setDeviceNumber] = useState("");
  const [serviceNumber, setServiceNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const trimmedSerial = deviceNumber.trim();
  const trimmedService = serviceNumber.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trimmedSerial && !trimmedService) return;

    setLoading(true);
    setError(null);

    const isSerialSearch = Boolean(trimmedSerial);

    try {
      const result = isSerialSearch
        ? await lookupDeviceBySerial(trimmedSerial)
        : await lookupDeviceByServiceNumber(trimmedService);

      if (!result.pairzonId) {
        setError(isSerialSearch ? SERIAL_NOT_FOUND_ERROR : SERVICE_NOT_FOUND_ERROR);
        return;
      }

      // Tag the redirect so the destination page knows it was reached via
      // the /device serial lookup rather than a direct QR code scan.
      // QR codes must keep linking straight to /device/<pairzonId> with no query string.
      window.location.href = `${PORTAL_BASE_URL}/device/${result.pairzonId}?source=device`;
    } catch (err) {
      if (err.response?.status === 404) {
        setError(isSerialSearch ? SERIAL_NOT_FOUND_ERROR : SERVICE_NOT_FOUND_ERROR);
      } else {
        setError(GENERIC_ERROR);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f5f5f5",
      }}
      dir="rtl"
    >
      <Navbar />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{ p: 4, maxWidth: 480, width: "100%", borderRadius: 2 }}
        >
          <Typography
            variant="h5"
            sx={{ mb: 1, color: "#006699", fontWeight: 700, textAlign: "center" }}
          >
            חיפוש מכשיר
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 3, color: "#5f6368", textAlign: "center" }}
          >
            הזן/י את המספר הסידורי של המכשיר / מספר שירות כדי לפתוח קריאת שירות
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="מספר סידורי"
              value={deviceNumber}
              onChange={(e) => setDeviceNumber(e.target.value)}
              disabled={loading || Boolean(trimmedService)}
              inputProps={{ dir: "ltr" }}
              sx={{ mb: 1 }}
              autoFocus
            />
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 2, color: "#5f6368" }}
            >
              המספר הסידורי מופיע על גבי מדבקה המודבקת למכשיר, ולעיתים הוא
              מסומן באותיות באנגלית S/N.
            </Typography>

            <TextField
              fullWidth
              label="מספר שירות/ מספר מכבי"
              value={serviceNumber}
              onChange={(e) => setServiceNumber(e.target.value)}
              disabled={loading || Boolean(trimmedSerial)}
              inputProps={{ dir: "ltr" }}
              sx={{ mb: 2 }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || (!trimmedSerial && !trimmedService)}
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SearchIcon />
                )
              }
              sx={{
                backgroundColor: "#006699",
                "&:hover": { backgroundColor: "#004d73" },
                py: 1.5,
              }}
            >
              {loading ? "מחפש..." : "חיפוש"}
            </Button>
          </Box>
        </Paper>
      </Box>
      <Footer />
    </Box>
  );
};

export default DeviceLookup;
